import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "../api/client";
import {
  IconAlert,
  IconLoader,
} from "../components/Icons";
import BranchSelector from "../components/BranchSelector";
import ColorPicker from "../components/ColorPicker";
import SearchableSelect from "../components/SearchableSelect";
import {
  Branch,
  Category,
  Product,
  Tag,
  UNIT_LABELS,
  VatRate,
  Warehouse,
} from "./Catalog";

function ProductForm({
  product,
  categories,
  vatRates,
  branches,
  warehouses,
  tags,
  onSave,
  onCancel,
  onImageChange,
}: {
  product: Product | null;
  categories: Category[];
  vatRates: VatRate[];
  branches: Branch[];
  warehouses: Warehouse[];
  tags: Tag[];
  onSave: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  onImageChange?: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(product?.name || "");
  const [altName, setAltName] = useState(product?.alt_name || "");
  const [code, setCode] = useState(product?.code || "");
  const [barcode, setBarcode] = useState(
    product?.barcodes?.length ? product.barcodes.join(", ") : product?.barcode || ""
  );
  const [categoryId, setCategoryId] = useState(product?.category_id || "");
  const [vatRateId, setVatRateId] = useState(product?.vat_rate_id || "");
  const [price, setPrice] = useState(product ? String(product.price / 100) : "");
  const [unit, setUnit] = useState(product?.unit || "ks");
  const [description, setDescription] = useState(product?.description || "");
  const [subtitle, setSubtitle] = useState(product?.subtitle || "");
  const [color, setColor] = useState<string | null>(product?.color || null);
  const [productType, setProductType] = useState(product?.product_type || "stockable");
  const [isSellable, setIsSellable] = useState(product?.is_sellable ?? true);
  const [deductionSources, setDeductionSources] = useState<
    { source_product_id: string; quantity: string }[]
  >(
    product?.deduction_sources.map((ds) => ({
      source_product_id: ds.source_product_id,
      quantity: String(ds.quantity),
    })) || []
  );
  const [sourceOptions, setSourceOptions] = useState<Record<number, Product[]>>({});
  const [branchIds, setBranchIds] = useState<string[]>(product?.branch_ids || []);
  const [warehouseIds, setWarehouseIds] = useState<string[]>(product?.warehouse_ids || []);
  const [tagIds, setTagIds] = useState<string[]>(product?.tags || []);
  const defaultedBranchIds = useRef(false);

  useEffect(() => {
    if (
      !product &&
      branches.length > 0 &&
      branchIds.length === 0 &&
      productType !== "inventory_only" &&
      !defaultedBranchIds.current
    ) {
      setBranchIds(branches.map((b) => b.id));
      defaultedBranchIds.current = true;
    }
  }, [branches, product, branchIds.length, productType]);

  const [isActive, setIsActive] = useState(product?.is_active ?? true);
  const [excludeFromRevenue, setExcludeFromRevenue] = useState(product?.exclude_from_revenue ?? false);
  const [cashPayout, setCashPayout] = useState(product?.cash_payout ?? false);
  const [enterPriceAtPos, setEnterPriceAtPos] = useState(product?.enter_price_at_pos ?? false);
  const [orderGroup, setOrderGroup] = useState(product?.order_group || "");
  const [imageUrl, setImageUrl] = useState<string | null>(product?.image_url || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageErr, setImageErr] = useState<string | null>(null);
  const isInventoryOnly = productType === "inventory_only";
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Re-encode through <canvas> so formats Pillow can't read (AVIF, HEIC…)
  // but the browser can render upload reliably — the server stores WEBP.
  async function normalizeImage(f: File): Promise<File> {
    try {
      const bmp = await createImageBitmap(f);
      const maxDim = 1024;
      const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bmp.width * scale));
      canvas.height = Math.max(1, Math.round(bmp.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return f;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
      bmp.close();
      for (const type of ["image/webp", "image/jpeg"] as const) {
        const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, type, 0.9));
        if (blob) {
          const ext = type === "image/webp" ? ".webp" : ".jpg";
          const base = f.name.replace(/\.[^.]+$/, "") || "image";
          return new File([blob], base + ext, { type });
        }
      }
      return f;
    } catch {
      return f;
    }
  }

  async function handleImageSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setImageErr(t("productForm.selectImage"));
      return;
    }
    if (f.size > 3 * 1024 * 1024) {
      setImageErr(t("productForm.imageTooLarge"));
      return;
    }
    const normalized = await normalizeImage(f);
    setImageFile(normalized);
    setImageErr(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageUrl(ev.target?.result as string | null);
    };
    reader.onerror = () => {
      setImageErr(t("productForm.previewError"));
    };
    reader.readAsDataURL(normalized);
  }

  async function uploadImage() {
    if (!imageFile || !product) return;
    setImageUploading(true);
    setImageErr(null);
    try {
      const form = new FormData();
      form.append("file", imageFile);
      const { data } = await api.post(`/catalog/products/${product.id}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(data.image_url);
      setImageFile(null);
      onImageChange?.();
    } catch (e: any) {
      setImageErr(e?.response?.data?.detail || t("productForm.uploadError"));
    } finally {
      setImageUploading(false);
    }
  }

  async function removeImage() {
    if (!product) return;
    setImageUploading(true);
    setImageErr(null);
    try {
      await api.delete(`/catalog/products/${product.id}/image`);
      setImageUrl(null);
      setImageFile(null);
      onImageChange?.();
    } catch (e: any) {
      setImageErr(e?.response?.data?.detail || t("productForm.removeImageError"));
    } finally {
      setImageUploading(false);
    }
  }

  useEffect(() => {
    if (product?.deduction_sources) {
      const initialOptions: Record<number, Product[]> = {};
      product.deduction_sources.forEach((ds, idx) => {
        if (ds.source_product_name && ds.source_product_id) {
          initialOptions[idx] = [
            {
              id: ds.source_product_id,
              name: ds.source_product_name,
              alt_name: null,
              code: null,
              barcode: null,
              category_id: null,
              vat_rate_id: null,
              price: 0,
              cost: null,
              unit: "ks",
              description: null,
              subtitle: null,
              color: null,
              image_url: null,
              is_sellable: true,
              product_type: "inventory_only",
              deduction_sources: [],
              has_variants: false,
              is_active: true,
              exclude_from_revenue: false,
              cash_payout: false,
              enter_price_at_pos: false,
              order_group: null,
              sort_order: 0,
              variants: [],
              branch_ids: [],
              warehouse_ids: [],
              tags: [],
            },
          ];
        }
      });
      setSourceOptions(initialOptions);
    }
  }, [product]);

  async function searchSourceProducts(idx: number, query: string) {
    if (!query.trim()) {
      setSourceOptions((prev) => ({ ...prev, [idx]: [] }));
      return;
    }
    try {
      const { data } = await api.get<Product[]>(
        `/catalog/products?limit=20&search=${encodeURIComponent(query)}`
      );
      const filtered = data.filter(
        (p) =>
          (p.product_type === "stockable" || p.product_type === "inventory_only") &&
          p.id !== product?.id
      );
      setSourceOptions((prev) => ({ ...prev, [idx]: filtered }));
    } catch {
      setSourceOptions((prev) => ({ ...prev, [idx]: [] }));
    }
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const data: Record<string, unknown> = {
      name,
      alt_name: altName.trim() || null,
      code: code || null,
      barcodes: barcode.split(",").map((b) => b.trim()).filter(Boolean),
      category_id: categoryId || null,
      vat_rate_id: isInventoryOnly ? null : vatRateId || null,
      price: isInventoryOnly ? 0 : Math.round(parseFloat(price || "0") * 100),
      unit,
      description: description || null,
      subtitle: subtitle.trim() || null,
      color: isInventoryOnly ? null : color || null,
      product_type: productType,
      is_sellable: isSellable,
      deduction_sources: isInventoryOnly
        ? []
        : deductionSources.map((ds) => ({
            source_product_id: ds.source_product_id,
            quantity: parseFloat(ds.quantity),
          })),
      branch_ids: branchIds,
      warehouse_ids: warehouseIds,
      tag_ids: tagIds,
      is_active: isActive,
      exclude_from_revenue: excludeFromRevenue || cashPayout,
      cash_payout: cashPayout,
      enter_price_at_pos: enterPriceAtPos,
      // "" clears the override — ProductUpdate uses exclude_none so null
      // would be dropped instead of clearing the column.
      order_group: orderGroup.trim(),
    };
    onSave(data);
  }

  return (
    <div className="card" style={{ padding: 24, marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>
        {product ? t("catalog.editProduct") : t("catalog.newProduct")}
      </h3>
      <form
        onSubmit={submit}
        style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 300,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
          }}
        >
          <div style={{ gridColumn: "1 / -1" }}>
            <label>{t("productForm.productType")}</label>
            <SearchableSelect
              value={productType}
              onChange={(val) => {
                setProductType(val);
                if (val === "inventory_only") {
                  setIsSellable(false);
                  setBranchIds([]);
                } else if (productType === "inventory_only") {
                  setIsSellable(true);
                }
                if (val === "non_stockable") {
                  setWarehouseIds([]);
                }
              }}
              options={[
                { value: "stockable", label: t("productForm.type.stockable") },
                { value: "non_stockable", label: t("productForm.type.non_stockable") },
                { value: "weighed", label: t("productForm.type.weighed") },
                { value: "inventory_only", label: t("productForm.type.inventory_only") },
              ]}
              searchPlaceholder={t("productForm.searchType")}
            />
          </div>
          <div>
            <label>{t("common.name")} *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <label>{t("productForm.altName")}</label>
            <input
              value={altName}
              onChange={(e) => setAltName(e.target.value)}
              placeholder={t("productForm.altNamePh")}
            />
          </div>
          <div>
            <label>{t("productForm.codeSku")}</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("productForm.codePh")} />
          </div>
          <div>
            <label>Barcode</label>
            <input value={barcode} onChange={(e) => setBarcode(e.target.value)} placeholder={t("productForm.barcodePh")} />
          </div>
          <div>
            <label>{t("catalog.orderGroup")}</label>
            <input
              list="order-group-options"
              value={orderGroup}
              onChange={(e) => setOrderGroup(e.target.value)}
              placeholder={t("catalog.orderGroupInheritPh")}
              maxLength={32}
            />
            <datalist id="order-group-options">
              {[...new Set(categories.map((c) => c.order_group).filter((g): g is string => !!g))].map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{t("catalog.orderGroupProductHint")}</div>
          </div>
          <div>
            <label>{t("catalog.colCategory")}</label>
            <SearchableSelect
              value={categoryId}
              onChange={setCategoryId}
              options={[
                { value: "", label: "—" },
                ...categories.map((c) => ({ value: c.id, label: c.name })),
              ]}
              placeholder="—"
              searchPlaceholder={t("catalog.searchCategory")}
            />
          </div>
          {!isInventoryOnly && (
            <div>
              <label>{t("orders.colPrice")} (Kč){!isInventoryOnly && !enterPriceAtPos ? " *" : ""}</label>
              <input
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required={!isInventoryOnly && !enterPriceAtPos}
              />
            </div>
          )}
          {!isInventoryOnly && (
            <div>
              <label>{t("productForm.vatRate")}</label>
              <SearchableSelect
                value={vatRateId}
                onChange={setVatRateId}
                options={[
                  { value: "", label: "—" },
                  ...vatRates.map((v) => ({ value: v.id, label: `${v.name} (${v.rate}%)` })),
                ]}
                placeholder="—"
                searchPlaceholder={t("productForm.searchVat")}
              />
            </div>
          )}
          <div>
            <label>{t("productForm.unit")}</label>
            <SearchableSelect
              value={unit}
              onChange={setUnit}
              options={Object.entries(UNIT_LABELS).map(([k, v]) => ({ value: k, label: v }))}
              searchPlaceholder={t("productForm.searchUnit")}
            />
          </div>
          {!isInventoryOnly && (
            <div>
              <label>{t("customers.color")}</label>
              <ColorPicker value={color} onChange={setColor} placeholder="#ff6b6b" />
            </div>
          )}

          {productType !== "inventory_only" && (
            <div style={{ gridColumn: "1 / -1" }}>
              <BranchSelector
                branches={branches}
                selected={branchIds}
                onChange={setBranchIds}
                label={t("catalog.salesLocations")}
              />
            </div>
          )}
          {productType !== "non_stockable" && (
            <div style={{ gridColumn: "1 / -1" }}>
              <BranchSelector
                branches={warehouses.map((w) => ({
                  id: w.id,
                  name: `${w.name}${w.code ? ` (${w.code})` : ""}`,
                }))}
                selected={warehouseIds}
                onChange={setWarehouseIds}
                label={t("nav.warehouses")}
              />
            </div>
          )}
          {productType !== "inventory_only" && tags.length > 0 && (
            <div style={{ gridColumn: "1 / -1" }}>
              <BranchSelector
                branches={tags.map((tg) => ({ id: tg.id, name: tg.name }))}
                selected={tagIds}
                onChange={setTagIds}
                label={t("catalog.tags")}
              />
            </div>
          )}
          {productType === "weighed" && (
            <div style={{ gridColumn: "1 / -1" }}>
              <label>{t("productForm.deduction")}</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {deductionSources.map((ds, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ flex: 2 }}>
                      <SearchableSelect
                        value={ds.source_product_id}
                        onChange={(val) => {
                          const newDs = [...deductionSources];
                          newDs[idx].source_product_id = val;
                          setDeductionSources(newDs);
                        }}
                        onSearch={(query) => searchSourceProducts(idx, query)}
                        options={(sourceOptions[idx] || []).map((p) => ({
                          value: p.id,
                          label: `${p.name}${p.code ? ` (${p.code})` : ""}`,
                        }))}
                        placeholder={t("productForm.selectSource")}
                        searchPlaceholder={t("productForm.searchProduct")}
                        emptyMessage={
                          ds.source_product_id ? t("productForm.productFound") : t("productForm.typeToSearch")
                        }
                      />
                    </div>
                    <input
                      type="number"
                      step="0.001"
                      value={ds.quantity}
                      onChange={(e) => {
                        const newDs = [...deductionSources];
                        newDs[idx].quantity = e.target.value;
                        setDeductionSources(newDs);
                      }}
                      placeholder={t("productForm.quantity")}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="ghost"
                      style={{ padding: "8px 12px", color: "var(--red)" }}
                      onClick={() => setDeductionSources(deductionSources.filter((_, i) => i !== idx))}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="ghost"
                  style={{ fontSize: 12, padding: "6px 12px" }}
                  onClick={() =>
                    setDeductionSources([...deductionSources, { source_product_id: "", quantity: "" }])
                  }
                >
                  {t("productForm.addSource")}
                </button>
              </div>
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <label>{t("productForm.description")}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
            <label style={{ marginTop: 8 }}>{t("productForm.subtitle")}</label>
            <textarea value={subtitle} onChange={(e) => setSubtitle(e.target.value)} rows={2} placeholder={t("productForm.subtitlePh")} />
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>{t("productForm.subtitleHint")}</div>
          </div>
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12 }}>
            <button type="submit" className="primary">
              {product ? t("customers.saveChanges") : t("productForm.createProduct")}
            </button>
            <button type="button" className="ghost" onClick={onCancel}>
              {t("common.cancel")}
            </button>
          </div>
        </div>
        <div
          style={{
            width: 260,
            flexShrink: 0,
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            background: "var(--bg-elevated)",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
            boxSizing: "border-box",
          }}
        >
          {!isInventoryOnly && (
            <div>
              <label style={{ display: "block", textAlign: "center" }}>{t("productForm.image")}</label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageSelect}
                style={{ display: "none" }}
              />
              <div
                onClick={() => {
                  if (!imageUrl && product) fileInputRef.current?.click();
                }}
                style={{
                  width: 160,
                  height: 160,
                  margin: "0 auto",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--bg)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  cursor: imageUrl || !product ? "default" : "pointer",
                }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="náhled"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 12,
                      color: "var(--muted)",
                      fontSize: 12,
                      pointerEvents: "none",
                    }}
                  >
                    <div>{product ? t("productForm.clickToAddImage") : t("productForm.noImage")}</div>
                    {!product && (
                      <div style={{ marginTop: 6 }}>{t("productForm.imageAfterSave")}</div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                {imageFile && (
                  <button
                    type="button"
                    className="primary"
                    onClick={uploadImage}
                    disabled={imageUploading}
                  >
                    {imageUploading ? t("productForm.uploading") : t("productForm.uploadImage")}
                  </button>
                )}
                {product && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={removeImage}
                    disabled={imageUploading}
                    style={{ color: "var(--red)" }}
                  >
                    {t("productForm.removeImage")}
                  </button>
                )}
                {!imageFile && product && (
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {imageUrl ? t("productForm.changeImage") : t("productForm.chooseImage")}
                  </button>
                )}
              </div>
              {imageErr && (
                <div style={{ color: "var(--red)", fontSize: 12, marginTop: 6 }}>{imageErr}</div>
              )}
            </div>
          )}
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", width: "100%" }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <span style={{ flex: 1, textAlign: "left", minWidth: 0, overflowWrap: "break-word" }}>{t("productForm.isActive")}</span>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              cursor: isInventoryOnly ? "not-allowed" : "pointer",
              opacity: isInventoryOnly ? 0.6 : 1,
              width: "100%",
            }}
          >
            <input
              type="checkbox"
              checked={isSellable}
              onChange={(e) => setIsSellable(e.target.checked)}
              disabled={isInventoryOnly}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <span style={{ flex: 1, textAlign: "left", minWidth: 0, overflowWrap: "break-word" }}>{t("productForm.showOnPos")}</span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", width: "100%" }}>
            <input
              type="checkbox"
              checked={excludeFromRevenue || cashPayout}
              onChange={(e) => setExcludeFromRevenue(e.target.checked)}
              disabled={cashPayout}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <span style={{ flex: 1, textAlign: "left", minWidth: 0, overflowWrap: "break-word" }}>{t("productForm.excludeRevenue")}</span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", width: "100%" }}>
            <input
              type="checkbox"
              checked={cashPayout}
              onChange={(e) => {
                setCashPayout(e.target.checked);
                if (e.target.checked) setExcludeFromRevenue(true);
              }}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <span style={{ flex: 1, textAlign: "left", minWidth: 0, overflowWrap: "break-word" }}>
              {t("productForm.cashPayout")}
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                {t("productForm.cashPayoutHint")}
              </div>
            </span>
          </label>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", width: "100%" }}>
            <input
              type="checkbox"
              checked={enterPriceAtPos}
              onChange={(e) => setEnterPriceAtPos(e.target.checked)}
              style={{ flexShrink: 0, marginTop: 2 }}
            />
            <span style={{ flex: 1, textAlign: "left", minWidth: 0, overflowWrap: "break-word" }}>{t("productForm.enterPriceAtPos")}</span>
          </label>
        </div>
      </form>
    </div>
  );
}

export default function ProductFormPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [vatRates, setVatRates] = useState<VatRate[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  async function loadData() {
    setLoading(true);
    setErr(null);
    try {
      const [cats, vats, branchList, warehouseList, tagList, prod] = await Promise.all([
        api.get<Category[]>("/catalog/categories").then((r) => r.data),
        api.get<VatRate[]>("/catalog/vat-rates").then((r) => r.data),
        api.get<Branch[]>("/branches").then((r) => r.data),
        api.get<Warehouse[]>("/warehouses").then((r) => r.data),
        api.get<Tag[]>("/catalog/tags").then((r) => r.data),
        isEdit ? api.get<Product>(`/catalog/products/${id}`).then((r) => r.data) : Promise.resolve(null),
      ]);
      setCategories(cats);
      setVatRates(vats);
      setBranches(branchList);
      setWarehouses(warehouseList);
      setTags(tagList);
      setProduct(prod);
    } catch (e) {
      setErr(apiError(e, t("staff.loadError")));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave(data: Record<string, unknown>) {
    setErr(null);
    try {
      if (isEdit) {
        await api.put(`/catalog/products/${id}`, data);
      } else {
        await api.post("/catalog/products", data);
      }
      navigate("/catalog/products");
    } catch (e) {
      setErr(apiError(e, t("productForm.saveError")));
    }
  }

  function handleCancel() {
    navigate("/catalog/products");
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h1>{isEdit ? t("catalog.editProduct") : t("catalog.newProduct")}</h1>
      </div>

      {err && (
        <div
          className="card"
          style={{
            padding: "12px 16px",
            marginBottom: 20,
            background: "var(--red-dim)",
            color: "var(--red)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 14,
          }}
        >
          <IconAlert size={16} /> {err}
        </div>
      )}

      {isEdit && !product ? (
        <div className="card" style={{ padding: 48, textAlign: "center" }}>
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>{t("productForm.notFound")}</h3>
          <button className="primary" style={{ marginTop: 16 }} onClick={handleCancel}>
            {t("catalog.backToList")}
          </button>
        </div>
      ) : (
        <ProductForm
          product={product}
          categories={categories}
          vatRates={vatRates}
          branches={branches}
          warehouses={warehouses}
          tags={tags}
          onSave={handleSave}
          onCancel={handleCancel}
          onImageChange={loadData}
        />
      )}
    </div>
  );
}
